import React, { useEffect, useState } from "react";
import { useNavigate, useLocation, Navigate } from "react-router-dom";
import { useAuth } from "@/context/AuthContext";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { Eye, EyeOff, Lock, Mail } from "lucide-react";
import { toast } from "sonner";

export default function Login() {
  const { login, status } = useAuth();
  const navigate = useNavigate();
  const location = useLocation();
  const [email, setEmail] = useState("");
  const [password, setPassword] = useState("");
  const [showPw, setShowPw] = useState(false);
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState("");
  const [flashMessage, setFlashMessage] = useState("");

  useEffect(() => {
    // Flash message legacy: cambio de contraseña.
    if (localStorage.getItem("fstudy_pwd_changed") === "1") {
      setFlashMessage("Su contraseña se actualizó correctamente. Vuelva a iniciar sesión con la nueva contraseña.");
      localStorage.removeItem("fstudy_pwd_changed");
      return;
    }
    // Fase 13 · Flash desde el interceptor (sesión cerrada / inactividad).
    const sessionFlash = localStorage.getItem("fstudy_login_flash");
    if (sessionFlash) {
      setFlashMessage(sessionFlash);
      localStorage.removeItem("fstudy_login_flash");
    }
  }, []);

  if (status === "authenticated") {
    const to = location.state?.from?.pathname || "/dashboard";
    return <Navigate to={to} replace />;
  }

  const submit = async (e) => {
    e.preventDefault();
    setError("");
    setLoading(true);
    const r = await login(email.trim(), password);
    setLoading(false);
    if (r.ok) {
      toast.success("Sesión iniciada");
      navigate(location.state?.from?.pathname || "/dashboard", { replace: true });
    } else {
      setError(r.error || "No pudimos iniciar sesión");
    }
  };

  return (
    <div className="min-h-screen flex bg-background" data-testid="login-page">
      {/* Left: Form */}
      <div className="flex-1 flex flex-col justify-center px-6 sm:px-12 lg:px-24 xl:px-32 bg-brand-radial">
        <div className="w-full max-w-md mx-auto animate-fade-in-up">
          <div className="flex items-center gap-3 mb-10">
            <img src="/assets/logo.png" alt="FStudy Manager" className="h-14 w-14 object-contain" />
            <div>
              <div className="font-serif-display text-2xl font-semibold text-brand-ink">
                FStudy <span className="text-primary">Manager</span>
              </div>
              <div className="text-[11px] uppercase tracking-[0.18em] text-stone-500">
                Create ART, Design & Marketing
              </div>
            </div>
          </div>

          <div className="divider-gold mb-5" />
          <h1 className="font-serif-display text-4xl sm:text-5xl font-semibold text-brand-ink leading-tight">
            Iniciar sesión
          </h1>
          <p className="mt-3 text-stone-600 text-sm">
            Ingrese sus credenciales para acceder a su panel de marketing colaborativo.
          </p>

          <form onSubmit={submit} className="mt-8 space-y-5" data-testid="login-form">
            {flashMessage && (
              <div
                className="text-sm rounded-md border border-brand-emerald/40 bg-brand-emerald-soft/50 px-3 py-2 text-brand-emerald-dark"
                data-testid="login-flash"
              >
                {flashMessage}
              </div>
            )}
            <div className="space-y-1.5">
              <Label htmlFor="email" className="text-xs uppercase tracking-widest font-semibold text-stone-600">
                Correo electrónico
              </Label>
              <div className="relative">
                <Mail className="absolute left-3 top-1/2 -translate-y-1/2 h-4 w-4 text-stone-400" />
                <Input
                  id="email"
                  type="email"
                  autoComplete="email"
                  required
                  value={email}
                  onChange={(e) => setEmail(e.target.value)}
                  placeholder="correo@empresa.com"
                  className="pl-10 h-11 bg-white"
                  data-testid="login-email-input"
                />
              </div>
            </div>

            <div className="space-y-1.5">
              <Label htmlFor="password" className="text-xs uppercase tracking-widest font-semibold text-stone-600">
                Contraseña
              </Label>
              <div className="relative">
                <Lock className="absolute left-3 top-1/2 -translate-y-1/2 h-4 w-4 text-stone-400" />
                <Input
                  id="password"
                  type={showPw ? "text" : "password"}
                  autoComplete="current-password"
                  required
                  value={password}
                  onChange={(e) => setPassword(e.target.value)}
                  placeholder="••••••••"
                  className="pl-10 pr-10 h-11 bg-white"
                  data-testid="login-password-input"
                />
                <button
                  type="button"
                  onClick={() => setShowPw((v) => !v)}
                  className="absolute right-3 top-1/2 -translate-y-1/2 text-stone-400 hover:text-stone-600"
                  data-testid="toggle-password-visibility"
                  aria-label="Mostrar contraseña"
                >
                  {showPw ? <EyeOff className="h-4 w-4" /> : <Eye className="h-4 w-4" />}
                </button>
              </div>
            </div>

            {error && (
              <div
                className="text-sm rounded-md border border-destructive/30 bg-destructive/10 px-3 py-2 text-destructive"
                data-testid="login-error"
              >
                {error}
              </div>
            )}

            <Button
              type="submit"
              disabled={loading}
              data-testid="login-submit-button"
              className="w-full h-11 bg-brand-gold text-brand-ink hover:bg-brand-gold-dark hover:text-white transition-all font-semibold text-sm tracking-wide shadow-gold active:scale-[0.98]"
            >
              {loading ? "Ingresando…" : "Iniciar sesión"}
            </Button>
          </form>

          <p className="mt-10 text-xs text-stone-400">
            © {new Date().getFullYear()} FStudy Manager · Acceso restringido al equipo interno.
          </p>
        </div>
      </div>

      {/* Right: Hero */}
      <div className="hidden lg:block relative flex-1 overflow-hidden">
        <img
          src="/assets/login-hero.jpg"
          alt=""
          className="absolute inset-0 h-full w-full object-cover"
        />
        <div className="absolute inset-0 bg-gradient-to-tr from-brand-emerald-dark/70 via-brand-ink/30 to-transparent" />
        <div className="absolute inset-0 flex flex-col justify-end p-12">
          <div className="max-w-md text-white">
            <div className="divider-gold mb-6" />
            <h2 className="font-serif-display text-3xl md:text-4xl font-medium leading-tight">
              Un solo lugar para pensar, crear y publicar marketing con arte.
            </h2>
            <p className="mt-4 text-white/85 text-sm leading-relaxed">
              Coordine campañas entre agentes, creativos y administradores con
              acceso granular por módulo y auditoría completa.
            </p>
          </div>
        </div>
      </div>
    </div>
  );
}
